import { ReactElement } from 'react';
import { Box, Card, Link, CardContent, Grid, Typography, Alert } from '@mui/material';
import {
  LeadsService,
  satisfiesVersionConstraint,
  useApiEffect,
  useAwsEffect,
  useAwsGetter,
  useDisableFeatures,
  useLocalstackStatus,
  useSnackbar,
} from '@localstack/services';
import {
  EmulatorType,
  LocalStackChaosFaults,
  LocalStackChaosNetworkEffect,
  LocalstackPlatformPlugin,
  PlanFamily,
  ProductFeature,
} from '@localstack/types';
import { FeatureUnavailableBox } from '@localstack/ui';
import { ExternalLink } from '@localstack/constants';

import { DEFAULT_CLIENT_OVERRIDES } from '../../constants';

import { InstanceViewProps } from '../props';

import {
  InternalServerErrorCard,
  LatencyCard,
  RegionUnavailableCard,
  ServiceUnavailableCard,
  DynamodbErrorCard,
  KinesisErrorCard,
} from './components/experiments';
import { EXPERIMENT_NAMES } from './constants';

const MIN_REQUIRED_VERSION = '3.6.0';

export const FISExperiments = ({
  Layout,
  clientOverrides = DEFAULT_CLIENT_OVERRIDES,
  useAuthProvider,
}: InstanceViewProps): ReactElement => {
  const { showSnackbar } = useSnackbar();

  const { running: isLocalStackRunning, version } = useLocalstackStatus(clientOverrides);
  const { hasFeature } = useAuthProvider();
  const hasAccessToChaosBasic = hasFeature(
    ProductFeature.FEATURE_LOCALSTACK_PLUGIN,
    LocalstackPlatformPlugin.LOCALSTACK_PLATFORM_PLUGIN_CHAOS_BASIC,
  );
  const hasAccessToChaosFull = hasFeature(
    ProductFeature.FEATURE_LOCALSTACK_PLUGIN,
    LocalstackPlatformPlugin.LOCALSTACK_PLATFORM_PLUGIN_CHAOS,
  );

  const { data: chaosFaults } = useAwsGetter('LocalStack', 'getChaosFaults', undefined, {
    clientOverrides,
    silentErrors: true,
  });

  const enableAction = useDisableFeatures({ endpoint: clientOverrides?.endpoint });

  const { data: chaosNetworkEffects } = useAwsGetter(
    'LocalStack',
    'getChaosNetworkEffects',
    undefined,
    { silentErrors: true, swrOverrides: { shouldRetryOnError: false }, clientOverrides },
    // when chaos-basic is loaded, getChaosNetworkEffects returns 404 as its not available
    // swrOverrides are set to prevent retries
  );

  const { createChaosEngineeringLead } = useApiEffect(LeadsService, ['createChaosEngineeringLead'], {
    suppressErrors: true,
  });

  const { addChaosNetworkEffect } = useAwsEffect('LocalStack', ['addChaosNetworkEffect'], {
    revalidate: ['getChaosNetworkEffects'],
    clientOverrides,
  });

  const { addChaosFaults, deleteChaosFaults } = useAwsEffect('LocalStack', ['addChaosFaults', 'deleteChaosFaults'], {
    revalidate: ['getChaosFaults'],
    clientOverrides,
  });

  const createLead = async (faultName: string) => {
    const res = await createChaosEngineeringLead({ chaos_fault_name: faultName });
    if (res) {
      showSnackbar({
        message: "We'll reach out to you soon",
        severity: 'success',
      });
    } else {
      showSnackbar({
        message: 'Something went wrong. Please try again later',
        severity: 'error',
      });
    }
  };

  const onUpsertFault = async (template: LocalStackChaosFaults, existingExperiment: LocalStackChaosFaults) => {
    await deleteChaosFaults(existingExperiment);
    await addChaosFaults(template);
  };

  const onUpsertNetworkEffect = async (template: LocalStackChaosNetworkEffect) => {
    await addChaosNetworkEffect(template);
  };

  const onStopFault = async (existingExperiment: LocalStackChaosFaults) => {
    await deleteChaosFaults(existingExperiment);
  };

  const onStopNetworkEffect = async (existingExperiment: LocalStackChaosNetworkEffect) => {
    // for now, there's only latency which has to be set to 0 for the experiment to be stopped
    await addChaosNetworkEffect({ ...existingExperiment, latency: 0 });
  };

  const LocalStackNotRunningAlert = !isLocalStackRunning
    ? 'LocalStack is not running. Start LocalStack to use this feature.'
    : undefined;
  const hasVersionMismatch = !satisfiesVersionConstraint(version || '', MIN_REQUIRED_VERSION);
  const VersionMismatchAlert = hasVersionMismatch ? `This feature requires version 2026.03 or higher.` : undefined;
  const alertMessage = LocalStackNotRunningAlert || VersionMismatchAlert;

  const chaosBasicExperiments = [
    {
      id: EXPERIMENT_NAMES.dynamodbError,
      content: (
        <DynamodbErrorCard
          onUpsertExperiment={onUpsertFault}
          onStopExperiment={onStopFault}
          alert={alertMessage}
          experiment={chaosFaults?.filter((fault) => fault.description === EXPERIMENT_NAMES.dynamodbError) || []}
          disabled={!enableAction}
        />
      ),
    },
    {
      id: EXPERIMENT_NAMES.kinesisError,
      content: (
        <KinesisErrorCard
          onUpsertExperiment={onUpsertFault}
          onStopExperiment={onStopFault}
          alert={alertMessage}
          experiment={chaosFaults?.filter((fault) => fault.description === EXPERIMENT_NAMES.kinesisError) || []}
          disabled={!enableAction}
        />
      ),
    },
  ];

  const chaosFullExperiments = [
    {
      id: EXPERIMENT_NAMES.internalServerError,
      content: (
        <InternalServerErrorCard
          onUpsertExperiment={onUpsertFault}
          onStopExperiment={onStopFault}
          alert={alertMessage}
          experiment={chaosFaults?.filter((fault) => fault.description === EXPERIMENT_NAMES.internalServerError) || []}
          disabled={!enableAction}
        />
      ),
    },
    {
      id: EXPERIMENT_NAMES.serviceUnavailableError,
      content: (
        <ServiceUnavailableCard
          onUpsertExperiment={onUpsertFault}
          onStopExperiment={onStopFault}
          alert={alertMessage}
          experiment={
            chaosFaults?.filter((fault) => fault.description === EXPERIMENT_NAMES.serviceUnavailableError) || []
          }
          disabled={!enableAction}
        />
      ),
    },
    {
      id: EXPERIMENT_NAMES.regionUnavailableError,
      content: (
        <RegionUnavailableCard
          onUpsertExperiment={onUpsertFault}
          onStopExperiment={onStopFault}
          alert={alertMessage}
          experiment={
            chaosFaults?.filter((fault) => fault.description === EXPERIMENT_NAMES.regionUnavailableError) || []
          }
          disabled={!enableAction}
        />
      ),
    },
    {
      id: EXPERIMENT_NAMES.latencyError,
      content: (
        <LatencyCard
          onUpsertExperiment={onUpsertNetworkEffect}
          onStopExperiment={onStopNetworkEffect}
          alert={alertMessage}
          experiment={chaosNetworkEffects || {}}
          disabled={!enableAction}
        />
      ),
    },
  ];

  const experiments = [...chaosBasicExperiments, ...(hasAccessToChaosFull ? chaosFullExperiments : [])];

  return (
    <Layout title="Chaos Engineering">
      {hasAccessToChaosBasic ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography>
                  Run controlled fault injection experiments to test resilience and performance.{' '}
                  <Link href={ExternalLink.DOCS_CHAOS_ENGINEERING} underline="always" target="_blank">
                    Learn more
                  </Link>
                </Typography>
                {alertMessage && (
                  <Box mt={2}>
                    <Box>
                      <Alert severity="error" style={{ width: '100%' }}>
                        {alertMessage}
                      </Alert>
                    </Box>
                  </Box>
                )}
                {experiments.map((experiment) => (
                  <Box mt={3} key={experiment.id}>
                    {experiment.content}
                  </Box>
                ))}
                <Box mt={3}>
                  {!hasAccessToChaosFull && (
                    <FeatureUnavailableBox
                      icon="chaosEngineering"
                      variant="bg-centered"
                      availableIn={{ family: PlanFamily.ULTIMATE_PLANS, emulators: [EmulatorType.AWS] }}
                      primaryButtonText="Request Access"
                      onFeatureUpgrade={() => createLead('global')} // TODO: change createLead definition in backend to allow creating leads without the fault name. Passing global for now
                      docsLink={ExternalLink.DOCS_CHAOS_ENGINEERING}
                      title="Advanced Chaos Experiments"
                      description="Upgrade to unlock chaos experiments for Internal Server Errors, Service Outages, Region Failures, Latency Injection, and more."
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <FeatureUnavailableBox
          icon="chaosEngineering"
          availableIn={{ family: PlanFamily.ULTIMATE_PLANS, emulators: [EmulatorType.AWS] }}
          primaryButtonText="Contact Sales"
          onFeatureUpgrade={() => window.open(ExternalLink.CONTACT_SALES, '_blank')}
          docsLink={ExternalLink.DOCS_CHAOS_ENGINEERING}
          title="Chaos Engineering"
          description="Run controlled fault injection experiments to test resilience and performance. Simulate outages, latency, and API errors to see how your system behaves under pressure—before it hits production."
        />
      )}
    </Layout>
  );
};
